import { Button, Form, Input, message, Select } from 'antd';
import styled from 'styled-components';
import { TableEvenOdd } from '../../account/styles';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { formatPhone } from '../../../utils/formatPhone';
import useGetEmployees from '../services/hooks/useGetEmployees';
import { PAGE_SIZE_EMPLOYEES } from '../services/hooks/useGetEmployees';
import { IEmployeeContent } from '../services/types';
import { useRef } from 'react';
import AddOrUpdateEmployeeModal, {
  AddOrUpdateEmployeeModalRef,
  RoleStatus,
} from './AddOrUpdateEmployeeModal';
import ModalConfirm, { ModalConfirmRef } from './ModalConfirm';
import accountApis from '../../../services/accountApis';
import { AxiosResponse } from 'axios';
import ModalResetPassword, { ModalResetPasswordRef } from './ModalResetPassword';
import { SyncOutlined } from '@ant-design/icons';

type FormSearchValues = {
  keySearch: string;
  roleName: string;
};

const EmployeesTableAndFilter = () => {
  const { page, setPage, getEmployees, data, loading } = useGetEmployees();
  const modalConfirmRef = useRef<ModalConfirmRef>(null);
  const modalResetPasswordRef = useRef<ModalResetPasswordRef>(null);
  const updateEmployeeModalRef = useRef<AddOrUpdateEmployeeModalRef>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const roles = [
    { label: 'All Roles', value: '' },
    { label: 'Manager', value: RoleStatus.MANAGER },
    { label: 'Sales', value: RoleStatus.SALE },
    { label: 'Marketing', value: RoleStatus.MARKETING },
    { label: 'Agent', value: RoleStatus.AGENT },
  ];

  const handleNavigateDetail = (id: string) => {
    navigate(`/employees/${id}`);
  };

  const handleOpenCreateOrUpdateEmployeeModal = (employeeData?: IEmployeeContent) => {
    updateEmployeeModalRef.current?.open(employeeData, () => {
      const values = form.getFieldsValue();
      getEmployees({ ...values, page });
    });
  };

  const handleFinishFormSearch = (values: FormSearchValues) => {
    setPage(1);
    getEmployees({ ...values, page: 1 });
  };

  const handleChangePage = (page: number) => {
    const values = form.getFieldsValue();
    setPage(page);
    getEmployees({ ...values, page });
  };

  const fetchDeleteEmployee = async (employeeId: string) => {
    try {
      const response: AxiosResponse<{ data: any }> = await accountApis.deleteEmployee(employeeId);
      if (response.data.data) {
        message.success('Employee deleted successfully');
        getEmployees({ ...form.getFieldsValue(), page });
        modalConfirmRef.current?.close();
      } else {
        message.error('Failed to delete employee');
      }
    } catch (error) {}
  };

  const handleOpenModalConfirmDelete = (employeeData: IEmployeeContent) => {
    modalConfirmRef.current?.open(
      'Do you want delete this employee?',
      'You can’t restore this file',
      () => fetchDeleteEmployee(employeeData.id)
    );
  };

  const handleOpenModalResetPassword = (employeeData: IEmployeeContent) => {
    modalResetPasswordRef.current?.open(employeeData);
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (shopName: any, employeeData) => (
        <Button type='link' onClick={() => handleNavigateDetail(employeeData.id)}>
          {shopName}
        </Button>
      ),
    },
    {
      title: <span>Phone</span>,
      dataIndex: 'phone',
      render: (text: any) => <span>{formatPhone(text)}</span>,
    },
    {
      title: <span>Email</span>,
      dataIndex: 'email',
      render: (email: any) => <span>{email}</span>,
    },

    {
      title: <span>Address</span>,
      dataIndex: 'address',
      render: (_: any, employeeData: IEmployeeContent) => {
        const result = `${employeeData?.address ? employeeData?.address : ''}${
          employeeData?.city ? `, ${employeeData?.city}` : ''
        }${employeeData?.state ? `, ${employeeData?.state}` : ''}${
          employeeData?.zip ? `, ${employeeData?.zip}` : ''
        }`;
        return <span>{result}</span>;
      },
    },
    {
      title: <span>Role</span>,
      dataIndex: 'roleName',
      render: (text: any) => <span>{text}</span>,
    },
    {
      title: <span>Created Date</span>,
      dataIndex: 'createdDate',
      render: (text: any) => <span>{text}</span>,
    },
    {
      title: <span>Actions</span>,
      width: '100px',
      dataIndex: 'id',
      render: (id: string, employeeData: IEmployeeContent) => (
        <ActionButtonsStyled>
          <ActionButtonStyled
            className='btn-edit'
            onClick={() => handleOpenCreateOrUpdateEmployeeModal(employeeData)}
          >
            <IcEdit />
          </ActionButtonStyled>
          <ActionButtonStyled
            className='btn-delete'
            onClick={() => handleOpenModalConfirmDelete(employeeData)}
          >
            <IcDelete />
          </ActionButtonStyled>
          <ActionButtonStyled
            className='btn-reset-password'
            onClick={() => handleOpenModalResetPassword(employeeData)}
          >
            <SyncOutlined style={{ fontSize: '16px',color: 'white' }} />
            {/* <IcResetPassword /> */}
          </ActionButtonStyled>
        </ActionButtonsStyled>
      ),
    },
  ];

  return (
    <ContainerStyled>
      <Form form={form} onFinish={handleFinishFormSearch}>
        <FilterContainerStyled>
          <div className='item'>
            <Form.Item name={'keySearch'} noStyle>
              <Input
                placeholder='Employee name/phone'
                style={{
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: '500',
                  borderRadius: '4px',
                }}
                suffix={<IcSearch />}
              />
            </Form.Item>
          </div>

          <div className='item'>
            <Form.Item name={'roleName'} noStyle initialValue={''}>
              <Select
                options={roles}
                style={{ width: '100%', height: '48px', borderRadius: '4px' }}
              />
            </Form.Item>
          </div>
          <SearchButtonStyled className='btn-search' onClick={() => form.submit()}>
            Search
          </SearchButtonStyled>
          <SearchButtonStyled
            className='btn-add'
            onClick={() => handleOpenCreateOrUpdateEmployeeModal()}
          >
            <div className='btn-icon-container'>
              <IcAdd />
              <span>Add Employee</span>
            </div>
          </SearchButtonStyled>
        </FilterContainerStyled>
      </Form>
      <TableContentStyled
        loading={loading}
        columns={columns}
        dataSource={data?.content || []}
        rowKey='id'
        bordered
        pagination={{
          current: page,
          total: data?.totalElements || 0,
          pageSize: PAGE_SIZE_EMPLOYEES,
          onChange: handleChangePage,
        }}
      />
      <ModalConfirm ref={modalConfirmRef} />
      <AddOrUpdateEmployeeModal ref={updateEmployeeModalRef} />
      <ModalResetPassword ref={modalResetPasswordRef} />
    </ContainerStyled>
  );
};

export default EmployeesTableAndFilter;

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FilterContainerStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  .item {
    flex: 1;
  }
`;

const SearchButtonStyled = styled.button`
  cursor: pointer;
  display: flex;
  height: 48px;
  padding: 16px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  transition: all 0.3s ease;
  &:hover {
    opacity: 0.8;
  }
  color: #fff;
  text-align: center;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  border: none;
  outline: none;

  &.btn-search {
    background: #008bff;
  }
  &.btn-add {
    background: #0da472;
  }
  .btn-icon-container {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const TableContentStyled = styled(TableEvenOdd)`
  && .action-dots {
    text-align: center;
  }

  && .center-data {
    text-align: center;
  }
`;

const ActionButtonsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionButtonStyled = styled.button`
  cursor: pointer;
  display: flex;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  aspect-ratio: 1/1;
  border-radius: 4px;
  border: none;
  outline: none;
  transition: all 0.3s ease;
  &.btn-edit {
    background: #008bff;
  }
  &.btn-delete {
    background: #f53f3f;
  }
  &.btn-reset-password {
    background: #0da472;
  }
  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IcEdit = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M0 23.4287H23.6116V21.1911H0V23.4287ZM8.26406 15.5971V12.5875L18.3863 3.59453L20.548 5.87802L11.2922 15.5971H8.26406ZM24 5.88025L18.4513 0.571533L5.9029 11.6432V17.8347H12.3028L24 5.88025Z'
      fill='white'
    />
  </svg>
);

const IcDelete = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='15' height='24' viewBox='0 0 15 24' fill='none'>
    <path
      d='M14.8471 8.16543C10.1204 8.16543 5.40089 8.16543 0.674148 8.16543C0.645016 8.16543 0.615883 8.16543 0.579468 8.16543C0.615883 8.4786 0.652299 8.78449 0.688715 9.09038C0.688715 9.09766 0.688715 9.11223 0.688715 9.11951C0.703281 9.24333 0.717847 9.36714 0.732413 9.49095L0.878076 10.7218C0.921775 11.1151 0.972756 11.5084 1.01646 11.8944C1.11842 12.7756 1.22038 13.6496 1.32963 14.5309C1.40974 15.2301 1.49714 15.9292 1.57726 16.6284C1.67922 17.4951 1.78118 18.3618 1.88315 19.2285C1.99968 20.1899 2.10892 21.1512 2.22545 22.1053C2.25459 22.3384 2.28372 22.5714 2.30557 22.7972C2.34198 23.125 2.56048 23.3289 2.88822 23.3289C3.55098 23.3289 4.21375 23.3289 4.88379 23.3289C4.97119 23.3289 5.01489 23.2706 5.07315 23.2196C5.08044 23.2124 5.07315 23.1905 5.07315 23.1687C5.02946 23.1905 4.99304 23.2051 4.94934 23.2196C4.93477 23.1177 4.97847 23.0885 5.0513 23.0813C5.11685 23.074 5.16783 23.1177 5.17512 23.1905C5.17512 23.2051 5.17512 23.2196 5.17512 23.2342C5.16055 23.3143 5.20425 23.3289 5.27708 23.3289C6.26759 23.3289 7.25809 23.3289 8.24859 23.3289C8.86766 23.3289 9.48673 23.3289 10.1131 23.3289C10.2078 23.3289 10.2587 23.2706 10.3243 23.2269C10.3316 23.2196 10.3316 23.1905 10.3316 23.1759C10.3097 23.1759 10.3024 23.1759 10.2952 23.1832C10.2733 23.1978 10.2442 23.2124 10.2223 23.2196C10.2223 23.2196 10.2005 23.1978 10.2005 23.1905C10.215 23.1614 10.2296 23.125 10.2515 23.1104C10.3316 23.0594 10.419 23.1104 10.4262 23.2051C10.4262 23.2342 10.419 23.2633 10.4335 23.2852C10.4408 23.307 10.4699 23.3289 10.4918 23.3435C10.4991 23.3507 10.5209 23.3435 10.5355 23.3435C11.242 23.3435 11.9484 23.3435 12.6622 23.3435C13.0263 23.3435 13.2302 23.1614 13.2739 22.8045C13.3905 21.7994 13.5143 20.7944 13.6308 19.782C13.7619 18.675 13.893 17.5679 14.0241 16.4609C14.1406 15.4558 14.2572 14.4508 14.381 13.4384C14.4975 12.4261 14.6213 11.4137 14.7451 10.4013C14.7524 10.3139 14.767 10.2265 14.7743 10.1319L14.9199 8.92287C14.9345 8.79906 14.9491 8.68253 14.9636 8.566C14.9709 8.49317 14.9855 8.42033 15 8.3475C15 8.28196 15 8.21641 15 8.15814C14.9345 8.15814 14.8908 8.16543 14.8471 8.16543ZM5.40818 21.4353C5.05859 21.4644 4.7527 21.2095 4.70171 20.8526C4.57062 19.8111 4.43952 18.7696 4.30843 17.7354C4.1919 16.7741 4.06808 15.82 3.94427 14.8586C3.82774 13.9555 3.71849 13.0524 3.60925 12.1493C3.52913 11.5229 3.4563 10.9039 3.37619 10.2775C3.3252 9.91337 3.57283 9.57107 3.9297 9.52009C4.30114 9.4691 4.63617 9.70945 4.68715 10.0809C4.77455 10.7582 4.86194 11.4355 4.94206 12.1202C5.04402 12.9432 5.14599 13.7661 5.24795 14.5891C5.37905 15.6452 5.51014 16.7012 5.64124 17.75C5.75777 18.7041 5.88158 19.6655 5.99811 20.6196C6.00539 20.6705 6.00539 20.7215 6.01268 20.7652C6.02724 21.1221 5.75777 21.4061 5.40818 21.4353ZM7.89172 21.4717C7.52028 21.5372 7.15613 21.2605 7.12699 20.889C7.11971 20.8308 7.11971 20.7652 7.11971 20.707C7.11971 17.9321 7.11971 15.1645 7.11971 12.3896C7.11971 12.3459 7.11971 12.3095 7.11971 12.2658V12.142C7.11971 12.1493 7.11971 12.1639 7.11971 12.1711C7.11971 11.5011 7.11971 10.831 7.11971 10.1683C7.11971 9.88424 7.23624 9.66575 7.49843 9.54194C7.91357 9.33801 8.40882 9.62205 8.44524 10.0809C8.44524 10.1246 8.44524 10.1683 8.44524 10.212C8.44524 10.5834 8.44524 10.9549 8.44524 11.3263V12.6518C8.44524 15.3611 8.44524 18.0632 8.44524 20.7725C8.44524 21.1439 8.24131 21.4061 7.89172 21.4717ZM12.196 10.2411C12.1014 11.0423 11.9994 11.8361 11.8974 12.6373C11.7882 13.504 11.6789 14.3706 11.5697 15.2373C11.4532 16.1914 11.3294 17.1455 11.2128 18.0996C11.0963 19.01 10.987 19.9204 10.8705 20.8308C10.8341 21.1439 10.6083 21.3843 10.3243 21.4353C10.0184 21.4863 9.73435 21.3333 9.60326 21.0566C9.53771 20.9109 9.53771 20.7652 9.55956 20.6123C9.69794 19.5052 9.83632 18.3909 9.97469 17.2839C10.0912 16.3735 10.2005 15.4704 10.317 14.56L10.4262 13.686C10.4335 13.6423 10.4408 13.5914 10.4408 13.5477C10.5719 12.4843 10.703 11.4283 10.8414 10.3649C10.856 10.2702 10.8632 10.1683 10.8778 10.0736C10.9361 9.71673 11.2638 9.47639 11.6207 9.52737C11.9848 9.56378 12.2397 9.88424 12.196 10.2411Z'
      fill='white'
    />
    <path
      d='M0.790751 6.62155C2.24009 6.19913 3.68943 5.77671 5.13149 5.35429L5.17519 5.33972C7.65873 4.61141 10.1277 3.89038 12.604 3.16935L12.8589 3.09652C12.8662 3.09652 12.8734 3.09652 12.8807 3.09652V3.08924L13.0337 3.04554L13.2303 2.98728C13.2303 2.98728 13.2376 2.98728 13.2376 2.99456L13.2449 2.98728L13.6236 2.87803C13.6309 2.87803 13.6382 2.87803 13.6382 2.87803C13.6382 2.87803 13.6382 2.87075 13.6455 2.87075L13.813 2.81976C13.9368 2.78335 14.0606 2.74693 14.1771 2.71052H14.1844C14.2645 2.68867 14.3446 2.65953 14.432 2.63769C14.3228 2.25168 14.2063 1.88024 14.1043 1.5088C14.0096 1.16649 13.711 1.00627 13.376 1.10095C12.3345 1.40684 11.3003 1.71273 10.2588 2.01134C10.135 2.04775 10.0112 2.08417 9.88737 2.1133C9.8728 2.07688 9.86552 2.04775 9.85824 2.0259C9.75627 1.6836 9.66159 1.34129 9.55963 1.00627C9.4868 0.765923 9.27559 0.627544 9.02068 0.656676C8.95513 0.663959 8.88958 0.685809 8.82403 0.707658C7.33099 1.14465 5.85252 1.57435 4.37405 2.00405C4.07544 2.09145 3.94434 2.34636 4.03174 2.64497C4.12642 2.97999 4.22838 3.31502 4.32306 3.65004C4.34491 3.71559 4.33035 3.74472 4.2648 3.75929C3.91521 3.86125 3.55834 3.96321 3.20875 4.06518C2.42217 4.29824 1.63559 4.5313 0.841733 4.75707C0.69607 4.80077 0.57954 4.8736 0.506709 5.0047C0.470293 5.07025 0.448444 5.15036 0.419312 5.21591C0.419312 5.25961 0.419312 5.31059 0.419312 5.35429C0.48486 5.57278 0.550408 5.79128 0.615956 6.01705C0.674221 6.22098 0.732486 6.41763 0.790751 6.62155ZM8.60554 1.90937C8.65652 2.09145 8.7075 2.25896 8.75849 2.44104C8.72935 2.45561 8.70022 2.46289 8.67109 2.47017C8.4089 2.55029 8.13942 2.6304 7.87723 2.70323C7.86994 2.70323 7.86266 2.71052 7.85538 2.71052L7.03239 2.95086C6.508 3.10381 5.98362 3.25675 5.45195 3.4097C5.40097 3.22762 5.34998 3.06011 5.29172 2.87075C6.39875 2.55029 7.49851 2.22983 8.60554 1.90937Z'
      fill='white'
    />
  </svg>
);

const IcResetPassword = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M0 23.4287H23.6116V21.1911H0V23.4287ZM8.26406 15.5971V12.5875L18.3863 3.59453L20.548 5.87802L11.2922 15.5971H8.26406ZM24 5.88025L18.4513 0.571533L5.9029 11.6432V17.8347H12.3028L24 5.88025Z'
      fill='white'
    />
  </svg>
);

const IcAdd = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M12 4.5C12.4142 4.5 12.75 4.83579 12.75 5.25V18.75C12.75 19.1642 12.4142 19.5 12 19.5C11.5858 19.5 11.25 19.1642 11.25 18.75V5.25C11.25 4.83579 11.5858 4.5 12 4.5Z'
      fill='white'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M4.5 12C4.5 11.5858 4.83579 11.25 5.25 11.25H18.75C19.1642 11.25 19.5 11.5858 19.5 12C19.5 12.4142 19.1642 12.75 18.75 12.75H5.25C4.83579 12.75 4.5 12.4142 4.5 12Z'
      fill='white'
    />
    <path
      d='M12 4C12.6904 4 13.25 4.55964 13.25 5.25V10.75H18.75C19.4404 10.75 20 11.3096 20 12C20 12.6904 19.4404 13.25 18.75 13.25H13.25V18.75C13.25 19.4404 12.6904 20 12 20C11.3096 20 10.75 19.4404 10.75 18.75V13.25H5.25C4.55964 13.25 4 12.6904 4 12C4 11.3096 4.55964 10.75 5.25 10.75H10.75V5.25C10.75 4.55964 11.3096 4 12 4ZM11.75 13.25V18.75C11.75 18.8881 11.8619 19 12 19C12.1381 19 12.25 18.8881 12.25 18.75V13.25H11.75ZM13.25 12.25H18.75C18.8881 12.25 19 12.1381 19 12C19 11.8619 18.8881 11.75 18.75 11.75H13.25V12.25ZM11.75 12.25H12.25V11.75H11.75V12.25ZM5.25 11.75C5.11193 11.75 5 11.8619 5 12C5 12.1381 5.11193 12.25 5.25 12.25H10.75V11.75H5.25ZM12 5C11.8619 5 11.75 5.11193 11.75 5.25V10.75H12.25V5.25C12.25 5.11193 12.1381 5 12 5Z'
      fill='white'
    />
  </svg>
);

const IcSearch = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M8.77405 1.70831C4.97709 1.70831 1.89905 4.78636 1.89905 8.58331C1.89905 12.3803 4.97709 15.4583 8.77405 15.4583C12.571 15.4583 15.649 12.3803 15.649 8.58331C15.649 4.78636 12.571 1.70831 8.77405 1.70831ZM0.649048 8.58331C0.649048 4.096 4.28673 0.458313 8.77405 0.458313C13.2614 0.458313 16.899 4.096 16.899 8.58331C16.899 13.0706 13.2614 16.7083 8.77405 16.7083C4.28673 16.7083 0.649048 13.0706 0.649048 8.58331Z'
      fill='#1D2129'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M13.3468 13.5462C13.5906 13.3018 13.9863 13.3013 14.2307 13.5451L17.1674 16.4742C17.4118 16.7179 17.4123 17.1136 17.1685 17.358C16.9248 17.6024 16.529 17.6029 16.2846 17.3592L13.3479 14.4301C13.1035 14.1864 13.103 13.7906 13.3468 13.5462Z'
      fill='#1D2129'
    />
  </svg>
);
